
import { motion } from "framer-motion";
import { ArrowRight, Mail, Sparkles } from "lucide-react";
import { CONTACT, STATS } from "../lib/data";
import { FaWhatsapp } from 'react-icons/fa';

export const Hero = () => {
    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-screen pt-32 pb-20 overflow-hidden"
        >
            {/* Background grid + glow */}
            <div className="absolute inset-0 bg-grid opacity-40" />
            <div className="absolute inset-0 bg-radial-glow" />
            <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-cyan-500/20 blur-[120px] animate-blob" />
            <div className="absolute top-40 -right-32 w-[450px] h-[450px] rounded-full bg-blue-600/20 blur-[120px] animate-blob" style={{ animationDelay: "3s" }} />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                {/* Eyebrow */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400 mb-8"
                >
                    <Sparkles size={13} />
                    Digital Web Studio for Local Businesses
                </motion.div>

                {/* Headline */}
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tighter leading-[0.95] max-w-5xl"
                >
                    Websites that turn{" "}
                    <br />
                    <span className="text-gradient-cyan">walk-bys</span>

                    into{" "}
                    <span className="relative inline-block">
                        <span className="text-gradient-soft">paying customers.</span>
                        <svg className="absolute -bottom-3 left-0 w-full" height="14" viewBox="0 0 300 14" fill="none">
                            <path d="M2 8 Q 75 2, 150 8 T 298 8" stroke="#00f0ff" strokeWidth="2" fill="none" strokeLinecap="round" />
                        </svg>
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.25 }}
                    className="mt-8 text-lg md:text-xl text-slate-400 max-w-2xl leading-relaxed"
                >
                    I help local businesses like salons, clinics, schools, and cafés get more calls, bookings, and customers with fast, high-converting websites..
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.4 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        data-testid="hero-cta-whatsapp"
                        href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
                            "Hi RS Digital Web Studio, I'd like to discuss a website for my business."
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-cyan-400 text-[#040814] font-bold hover:bg-cyan-300 transition-all hover:shadow-2xl hover:shadow-cyan-500/40"
                    >
                        <FaWhatsapp size={18} />
                        Get your website started
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a
                        data-testid="hero-cta-email"
                        href={`mailto:${CONTACT.email}?subject=Project enquiry`}
                        className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/15 text-white font-semibold hover:border-cyan-400/60 hover:bg-cyan-400/5 transition-all"
                    >
                        <Mail size={18} />
                        Email me
                    </a>
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/5"
                    data-testid="hero-stats"
                >
                    {STATS.map((s, i) => (
                        <div key={i} className="bg-[#040814] p-6 md:p-8">
                            <div className="text-4xl md:text-5xl font-black text-gradient-cyan tracking-tighter">
                                {s.value}
                            </div>
                            <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};
